import { useRef, ReactNode, useState, useEffect } from 'react';
import Recaptcha from 'react-google-recaptcha';
import { useFeatureFlags } from '../featureFlags';
import { RecaptchaWrapper } from './RecaptchaWrapper';
import { RecaptchaCompleteParam } from './RecaptchaCompleteParam';
import { AvailableFeatureFlags } from '../types/AvailableFeatureFlags';

interface UseRecaptchaParams<T> {
  onRecaptchaComplete(param: RecaptchaCompleteParam, formValues?: T): void;
  recaptchaKey: string;
}

interface UseRecaptcha<T> {
  // eslint-disable-next-line lines-around-comment
  /**
   * Call this function when you want to start a recaptcha challenge.
   */
  startRecaptchaChallenge(formValues?: T): void;

  /**
   * This component should be rendered in the component that needs to display the recaptcha challenge.
   */
  RecaptchaComponent: ReactNode;

  /**
   * This boolean should be used to determine if the recaptcha challenge is currently running.
   * This is useful for displaying a loading indicator while the recaptcha challenge is running.
   * Since we cannot know if the user clicks outside of the recaptcha challenge,
   * this value will be set to false 5 seconds after the recaptcha challenge is started or if the user
   * has actually completed the challenge.
   */
  isRecaptchaRunning: boolean;
}

function useRecaptcha<T = undefined>({
  onRecaptchaComplete,
  recaptchaKey,
}: UseRecaptchaParams<T>): UseRecaptcha<T> {
  const recaptchaRef = useRef<Recaptcha>(null);
  const [isRecaptchaRunning, setIsRecaptchaRunning] = useState(false);

  // Determine if the recaptcha is disabled through a feature flag
  const hasFeatureFlag = useFeatureFlags<AvailableFeatureFlags>();
  const recaptchaIsDisabled = hasFeatureFlag(
    AvailableFeatureFlags.disableRecaptcha
  );

  const [formValues, setFormValues] = useState<T>();
  const [startingRecaptcha, setStartingRecaptcha] = useState(false);

  // ReCAPTCHA will not tell us if the user has clicked outside of the ReCAPTCHA
  // window to close it. This useEffect will flip the isRecaptchaRunning state to false
  // after 5 seconds if the user has not completed the challenge.
  useEffect(() => {
    if (!isRecaptchaRunning) {
      return () => {};
    }

    const timeoutId = setTimeout(() => {
      setIsRecaptchaRunning(false);
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isRecaptchaRunning]);

  // This useEffect ensures that the Recaptcha Challenge is started
  // only after this hook is re-rendered after state updates from
  // the previous cycle. This is crucial for consuming components that dispatch
  // a redux update that affects the `onRecaptchaComplete` function in same
  // render where they call the `startRecaptchaChallenge`.
  // TODO: This is a hacky solution that tries to solve a problem that
  //       shouldn't be a problem if we follow certain patterns in our Redux code.
  //       The Giant Slayers have written their Redux code in a way that doesn't encounter this problem.
  //       We should refactor the code in this hook to not need the useEffect
  //       instead preferring a more direct approach where all actions are handled
  //       in the functions themselves.
  useEffect(() => {
    if (!startingRecaptcha) {
      return;
    }
    setStartingRecaptcha(false);

    // If recaptcha is disabled, simply call onRecaptchaComplete
    // with the g-recaptcha-override param and the form values
    if (recaptchaIsDisabled) {
      onRecaptchaComplete(
        {
          'g-recaptcha-override': '1',
        },
        formValues
      );
      return;
    }

    // Reset ReCAPTCHA (in case ReCAPTCHa window was open before)
    recaptchaRef.current?.reset();

    // Execute ReCAPTCHA (makes ReCAPTCHA window appear)
    recaptchaRef.current?.execute();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startingRecaptcha]);

  function startRecaptchaChallenge(values: T): void {
    setFormValues(values);
    setIsRecaptchaRunning(true);

    // Trigger the RecaptchaChallenge to start after
    // this hook is re-rendered
    setStartingRecaptcha(true);
  }

  function onGoogleRecaptchaChange(recaptchaId: string | null): void {
    setIsRecaptchaRunning(false);
    // If there is no recaptchaId, the user was did not complete the
    // challenge so do nothing.
    if (!recaptchaId) {
      return;
    }
    // If the user successfully completed the challenge, invoke
    // the supplied onRecaptchaComplete callback with the recaptcha
    // response and the form values.
    onRecaptchaComplete(
      {
        'g-recaptcha-response': recaptchaId,
      },
      formValues
    );
  }

  const RecaptchaComponent = RecaptchaWrapper({
    recaptchaKey,
    recaptchaRef,
    recaptchaIsDisabled,
    onGoogleRecaptchaChange,
  });

  return { startRecaptchaChallenge, RecaptchaComponent, isRecaptchaRunning };
}

export { useRecaptcha };
